import {IMessageProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {esWB_Product_UOM} from "../PA.Core/PAdbSchema";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import * as _ from "lodash"
import {DateUtils} from "../PA.Core/Basic/Dates";
import {WB_FinishedProductRow, WB_ProductUOMRow} from "../PA.Core/PAdbSchemaRows";
import {AuditLogType, IAuditService} from "../PA.Core/PA.Core.IF.Audit";


export enum IUOMDeterminationPolicy {
    DominantValue="DominantValue",
    Minimum="Minimum",
    Maximum="Maximum",
    Average="Average"
}

export interface IUOMCalculateOptions {
    WbProdIds: number[]
    bDeriveUOM?: boolean,
    BaseUOM?: string,
    ValuePolicy: IUOMDeterminationPolicy,
    FallbackValuePolicy: IUOMDeterminationPolicy,
    UOMs?: string[],
    EffectiveDate?: Date,
    bExcludeDiscontinued?: boolean,
    DiscontinuedDateMin?: Date,
    AuditLogger?: IAuditService
}

export interface IUOMCalcResults {
    Messages: IMessageProvider,
    BaseUOM?: string,
    dctUOMs: {[uomCd: string]: number}
}

export class ProductUOMUtils
{

    public static async CalculateUOMs(options: IUOMCalculateOptions): Promise<IUOMCalcResults> {
        let rv: IUOMCalcResults = {
            Messages: new MessageProvider(),
            dctUOMs: {}
        }

        //--
        let dbContext = new PAdbContext()
        let rr = new ESRecordResolver(dbContext, {})
        let logger = options.AuditLogger
        function auditLog(t: AuditLogType, m: string){
            if(logger)
                logger.LogSync(t, m)
        }
        auditLog(AuditLogType.Info, "Starting UOM calculations")
        let aEffDate: Date = (_.isDate(options?.EffectiveDate))? options.EffectiveDate: DateUtils.today
        let sEffDate = DateUtils.convertToSAPBW(aEffDate)

        let cond: any = {where: {WB_PROD_ID: {inq: options.WbProdIds}, EFFECTIVE_START_DT: {lte: sEffDate}, EFFECTIVE_END_DT: {gte: sEffDate}}}
        let arrProduct: WB_ProductUOMRow[] = <WB_ProductUOMRow[]> await  rr.queryAllWithRelationships(esWB_Product_UOM, cond)

        let dctProducts: {[wbProdId: number]: WB_FinishedProductRow} = {}
        let dctProdUOM: {[wbProdId: number]: {[UOMCd: string]: WB_ProductUOMRow}} = {}
        let dctUOMProd: {[UOMCd: string]: {[wbProdId: number]: WB_ProductUOMRow}} = {}

        _.forEach(arrProduct, (aUOMRow) => {
            if(!aUOMRow.isActive)
                return
            if(!aUOMRow.WB_ProductRow)
                return

            let wbProdId = aUOMRow.cif.WB_PROD_ID
            let uomCd = aUOMRow.cif.PRODUCT_UOM

            if(!(wbProdId in dctProducts))
                dctProducts[wbProdId] = aUOMRow.WB_ProductRow

            if(!(wbProdId in dctProdUOM))
                dctProdUOM[wbProdId] = {}

            if(!(uomCd in dctProdUOM[wbProdId]))
                dctProdUOM[wbProdId][uomCd] = aUOMRow

            if(!(uomCd in dctUOMProd))
                dctUOMProd[uomCd] = {}

            if(!(wbProdId in dctUOMProd[uomCd]))
                dctUOMProd[uomCd][wbProdId] = aUOMRow
        })

        let sTargetBaseUOM: string = options?.BaseUOM
        let allProducts = _.values(dctProducts)
        // Determine the Base UOM
        if(options.bDeriveUOM){

            let top1 = ProductUOMUtils.histogramValueOccurrence(allProducts, (a) => a.cif.BASE_UOM,1)
            if(top1.length == 0){
                let msg = `Unable to Derive Base UOM from ${allProducts.length} products`
                rv.Messages.addMessage({code: `No-BaseUOM`, type: MessageType.Error, description: msg, title: msg})
                auditLog(AuditLogType.Error, msg)
                return rv
            } else if(top1.length > 1){
                _.forEach(top1, (a) => {
                    if(!_.isEmpty(a.value)){
                        sTargetBaseUOM = a.value
                        return false
                    }
                })

                let baseUOMCandidates = _.map(top1, (a) => a.value)
                let msg = `Multiple Base UOMs found ${baseUOMCandidates.join(', ')} : chose ${sTargetBaseUOM}`
                rv.Messages.addMessage({code: `Multiple-BaseUOM`, type: MessageType.Information, description: msg, title: msg})
                auditLog(AuditLogType.Info, msg)
            } else {
                sTargetBaseUOM = top1[0].value
                let msg = `Base UOM chose ${sTargetBaseUOM} from ${top1[0].items.length} child products`
                rv.Messages.addMessage({code: `Chosen-BaseUOM`, type: MessageType.Information, description: msg, title: msg})
                auditLog(AuditLogType.Info, msg)
            }

        } else
            sTargetBaseUOM = options.BaseUOM

        if(_.isEmpty(sTargetBaseUOM)){
            let msg = `Base UOM is not valid (${sTargetBaseUOM})`
            rv.Messages.addMessage({code: `Invalid-BaseUOM`, type: MessageType.Error, description: msg, title: msg})
            auditLog(AuditLogType.Error, msg)
            return rv
        }
        rv.BaseUOM = sTargetBaseUOM

        // For each UOM, determine the Conversion Factor
        let totalProductCount = _.keys(dctProducts).length
        let uomCds: string[]
        if(_.isEmpty(options.UOMs) || !_.isArray(options.UOMs))
            uomCds = _.keys(dctUOMProd)
        else
            uomCds = options.UOMs

        for(let uomCd of uomCds){
            //
            auditLog(AuditLogType.Info, `Processing UOM ${uomCd}`)
            if(!(uomCd in dctUOMProd)){
                let msg = `UOM (${uomCd}) has no UOM conversions available from ${totalProductCount} products`
                rv.Messages.addMessage({code: `UOM-${uomCd}-NoPrds`, type: MessageType.Error, description: msg, title: msg})
                auditLog(AuditLogType.Error, msg)
                continue
            }


            let convFactors: {value: number, product: WB_FinishedProductRow}[] = []

            for(let wbPrdId in dctUOMProd[uomCd]){
                let product = dctProducts[<any>wbPrdId]

                let pUOMs = dctProdUOM[<any> wbPrdId] || {}
                //-- Find Product's conversion factor from sTargetBaseUOM to uomCd
                function getUOMProdConversionFactor(aUOMCd: string): number {
                    if(aUOMCd in pUOMs) {
                        let factor = pUOMs[aUOMCd].cif.CONVERSION_FACTOR
                        return factor
                    }

                    if(aUOMCd == product.cif.BASE_UOM)
                        return 1

                    if((aUOMCd == 'EA') && (product.cif.CASE_PACK_SIZE > 0) && (product.cif.BASE_UOM != 'EA'))
                        return product.cif.CASE_PACK_SIZE

                    let msg = `No Conversion Factor for UOM (${uomCd}), product ${product.cif.PRODUCT_ID}, ${product.cif.PRODUCT_NAME}.`
                    rv.Messages.addMessage({code: `UOM-${uomCd}-Prd-${product.cif.PRODUCT_ID}`, type: MessageType.Warning, description: msg, title: msg})
                    auditLog(AuditLogType.Warning, msg)

                    return undefined
                }

                let fromUOMCV = getUOMProdConversionFactor(sTargetBaseUOM)
                let toUOMCV = getUOMProdConversionFactor(uomCd)

                if((fromUOMCV > 0) && (toUOMCV > 0)){
                    //--
                    let cv = toUOMCV / fromUOMCV
                    convFactors.push({value: cv, product: product})
                }
            }

            if(convFactors.length) {
                let firstValue = convFactors[0].value
                let minValue = firstValue
                let maxValue = firstValue
                let sumValue = 0
                _.forEach(convFactors, (a) => {
                    minValue = Math.min(minValue, a.value)
                    maxValue = Math.max(maxValue, a.value)
                    sumValue += a.value
                })
                let avgValue = sumValue / convFactors.length
                let histTop1 = ProductUOMUtils.histogramValueOccurrence(convFactors, (a) => a.value.toFixed(2), 1)

                function getValueByDeterminationPolicy(policy: IUOMDeterminationPolicy): number {
                    switch (policy) {
                        case IUOMDeterminationPolicy.Minimum:
                            return minValue
                        case IUOMDeterminationPolicy.Maximum:
                            return maxValue
                        case IUOMDeterminationPolicy.Average:
                            return avgValue
                        case IUOMDeterminationPolicy.DominantValue:
                            if (histTop1.length == 1)
                                return histTop1[0].items[0].value
                            return undefined
                    }
                }

                let cv = getValueByDeterminationPolicy(options.ValuePolicy)
                if (_.isUndefined(cv)) {
                    cv = getValueByDeterminationPolicy(options.FallbackValuePolicy)
                    auditLog(AuditLogType.Info, `Using Fallback Determination Policy (${options.FallbackValuePolicy}) for UOM ${uomCd}`)
                }
                let sConversionValues =   convFactors.map(s => s.value).join(", ")
                auditLog(AuditLogType.Info, `UOM processor (${uomCd}) chose ${cv} from ${convFactors.length} input values, ${sConversionValues}`)
                rv.dctUOMs[uomCd] = cv
            }
            else // No Factors
            {
                let msg = `No conversion factors available to determine target UOM Conversion Factor ${uomCd}`
                rv.Messages.addMessage({code: `UOM-${uomCd}-NoConvFactors`, type: MessageType.Warning, description: msg, title: msg})
                auditLog(AuditLogType.Warning, msg)
            }

        } // Next UOMCds

        return rv
    }

    static histogramValueOccurrence<T>(items:  T[], indexer: string | ((a:T) => string), topN?: number): {value: string, items: T[], rank: number}[] {
        //--
        let rv: { [indexValue: string]: { value: string, items: T[], rank: number } } = {}

        _.forEach(items, (anItem) => {
            let indexValue: string

            if (_.isString(indexer))
                indexValue = (<any>anItem)[indexer]
            else
                indexValue = indexer(anItem)

            if (_.isEmpty(indexValue))
                return

            if (indexValue in rv)
                rv[indexValue].items.push(anItem)
            else
                rv[indexValue] = {value: indexValue, items: [anItem], rank: 0}
        })

        let groups = _.orderBy(_.values(rv), (a) => a.items.length, ['desc'])
        if(!_.isNumber(topN))
            return groups

        let topOccurences = groups[0].items.length
        let ranksTaken = 1

        let topGroups: { value: string, items: T[], rank: number }[] = []
        _.forEach(groups, (aGroup) => {
            if(_.isEmpty(topGroups)){
                aGroup.rank = ranksTaken
                topGroups.push(aGroup)
                return
            }

            if(aGroup.items.length == topOccurences){
                aGroup.rank = ranksTaken
                topGroups.push(aGroup)
                return
            }
            ranksTaken++
            if(ranksTaken > topN)
                return false

            topOccurences = aGroup.items.length
            topGroups.push(aGroup)

        })

        return topGroups
    }

}